/**
 * Financial Aid Enums - Shared enumeration types for the Financial Aid domain
 *
 * Per GRPO: All magic values defined here, never inline
 */
import { z } from 'zod';
import { GradeLevel as GradeLevelSchema, SchoolDivision as SchoolDivisionSchema } from '@triedge/domain-core';

export const GradeLevel = GradeLevelSchema;
export const SchoolDivision = SchoolDivisionSchema;

export type GradeLevel = z.infer<typeof GradeLevelSchema>;
export type SchoolDivision = z.infer<typeof SchoolDivisionSchema>;

// ============================================================================
// Financial Aid Status Workflow
// ============================================================================

export const FinancialAidStatus = z.enum([
  'invited',                  // Staff invited a family, no authenticated activity yet
  'opened',                   // Family opened the invite/auth link
  'started',                  // Family claimed invite and started editing
  'draft',                    // Parent started via legacy direct-entry flow
  'submitted',                // Parent completed and submitted
  'under_review',             // Staff reviewing
  'more_info_needed',         // Awaiting additional documents/info
  'approved',                 // Scholarship awarded
  'denied',                   // Application denied
  'award_accepted',           // Family accepted the award
  'award_declined',           // Family declined the award
  'closed',                   // Application closed with no further invite actions
]);
export type FinancialAidStatus = z.infer<typeof FinancialAidStatus>;

export const FinancialAidFamilyMatchType = z.enum([
  'none',
  'email',
  'email_last_name',
  'family_name',
  'manual',
]);
export type FinancialAidFamilyMatchType = z.infer<typeof FinancialAidFamilyMatchType>;

export const FinancialAidHydrationSource = z.enum([
  'none',
  'sis',
  'parent_account',
  'prior_application',
]);
export type FinancialAidHydrationSource = z.infer<typeof FinancialAidHydrationSource>;

// ============================================================================
// Appeals Workflow Status
// ============================================================================

export const AppealStatus = z.enum([
  'pending',                  // Appeal submitted, awaiting review
  'under_review',             // Staff actively reviewing
  'resolved',                 // Final decision made
]);
export type AppealStatus = z.infer<typeof AppealStatus>;

// ============================================================================
// Info Request Status
// ============================================================================

export const InfoRequestStatus = z.enum([
  'pending',                  // Requested, awaiting response
  'responded',                // Response submitted
  'closed',                   // Closed by staff/system
]);
export type InfoRequestStatus = z.infer<typeof InfoRequestStatus>;

// ============================================================================
// Appeal Event Types
// ============================================================================

export const AppealEventType = z.enum([
  'submitted',
  'reviewed',
  'decision_made',
  'closed',
]);
export type AppealEventType = z.infer<typeof AppealEventType>;

// ============================================================================
// Required Financial Documents (10 types per PRD)
// ============================================================================

export const RequiredFinancialDocumentType = z.enum([
  'tax_return_federal',       // Federal tax return (Form 1040)
  'tax_return_state',         // State tax return
  'w2_forms',                 // W-2 wage statements
  'pay_stubs',                // Recent pay stubs (last 3 months)
  'bank_statements',          // Bank statements (last 3 months)
  'investment_statements',    // Investment/brokerage statements
  'business_tax_return',      // Business tax returns (if applicable)
  'proof_of_benefits',        // Social Security, disability, etc.
  'mortgage_statement',       // Mortgage or rent payment proof
  'other_income_proof',       // Any other income documentation
  'other',                    // Optional extra uploads (letters, anything not requested)
]);
export type RequiredFinancialDocumentType = z.infer<typeof RequiredFinancialDocumentType>;

// ============================================================================
// Income Types
// ============================================================================

export const IncomeType = z.enum([
  'salary_wages',             // Employment income
  'self_employment',          // Business/self-employment income
  'rental_income',            // Rental property income
  'investment_income',        // Dividends, interest, capital gains
  'social_security',          // Social Security benefits
  'pension_retirement',       // Pension/retirement distributions
  'child_support',            // Child support received
  'alimony',                  // Alimony received
  'disability',               // Disability benefits
  'unemployment',             // Unemployment benefits
  'gift_support',             // Regular gifts from family
  'other',                    // Other income sources
]);
export type IncomeType = z.infer<typeof IncomeType>;

// ============================================================================
// Asset Types
// ============================================================================

export const AssetType = z.enum([
  'checking_savings',         // Bank accounts
  'investments',              // Stocks, bonds, mutual funds
  'retirement_accounts',      // 401k, IRA, etc.
  'real_estate',              // Property equity
  'vehicles',                 // Cars, boats, etc.
  'business_equity',          // Business ownership value
  'life_insurance_cash',      // Cash value of life insurance
  'other_assets',             // Other valuable assets
]);
export type AssetType = z.infer<typeof AssetType>;

// ============================================================================
// Expense Categories
// ============================================================================

export const ExpenseCategory = z.enum([
  'housing',                  // Mortgage/rent, utilities, maintenance
  'transportation',           // Car payments, gas, insurance
  'food',                     // Groceries, dining
  'healthcare',               // Medical, dental, prescriptions
  'insurance',                // Life, health, etc.
  'debt_payments',            // Credit cards, loans
  'childcare',                // Daycare, babysitting
  'tuition_other_schools',    // Non-YST tuition obligations
  'charitable_donations',     // Tzedakah, donations
  'discretionary',            // Entertainment, vacations
  'other',                    // Other expenses
]);
export type ExpenseCategory = z.infer<typeof ExpenseCategory>;

// ============================================================================
// Review Flag Types (10 types per PRD + FA-011)
// ============================================================================

export const ReviewFlagType = z.enum([
  'income_outlier',           // Income significantly higher/lower than average
  'income_change',            // Significant change from previous year
  'asset_heavy',              // High assets relative to income
  'expense_mismatch',         // Expenses don't align with stated income
  'high_discretionary',       // High discretionary spending
  'major_expenditures',       // Large planned purchases
  'simcha_planned',           // Major lifecycle event (wedding, etc.)
  'other_tuition_burden',     // Significant tuition at other schools
  'missing_documents',        // Required documents not submitted
  'appeal_pending',           // FA-011: Application has a pending appeal
]);
export type ReviewFlagType = z.infer<typeof ReviewFlagType>;

// ============================================================================
// Flag Severity
// ============================================================================

export const FlagSeverity = z.enum([
  'low',                      // Minor concern, informational
  'medium',                   // Moderate concern, warrants review
  'high',                     // Significant concern, requires attention
]);
export type FlagSeverity = z.infer<typeof FlagSeverity>;

// ============================================================================
// Confidence Level (for recommendations)
// ============================================================================

export const ConfidenceLevel = z.enum([
  'high',                     // Strong confidence in recommendation
  'medium',                   // Moderate confidence
  'low',                      // Low confidence, requires manual review
]);
export type ConfidenceLevel = z.infer<typeof ConfidenceLevel>;

// ============================================================================
// Decision Types
// ============================================================================

export const DecisionType = z.enum([
  'approve',                  // Accept recommendation as-is
  'override',                 // Custom scholarship percentage
  'request_more_info',        // Need additional information
  'deny',                     // Deny financial aid
]);
export type DecisionType = z.infer<typeof DecisionType>;
